const collectionSearchControllers = new WeakMap();
const collectionSearchState = new WeakMap();
const filterGroupControllers = new WeakMap();

function initCollectionSearch(root = document) {
  const collectionRoots = root.querySelectorAll('[data-collection-root]');

  collectionRoots.forEach(collectionRoot => {
    const previousController = collectionSearchControllers.get(collectionRoot);
    if (previousController) {
      previousController.abort();
    }

    const searchContainer = collectionRoot.querySelector('[data-collection-search]');
    const searchInput = searchContainer ? searchContainer.querySelector('[data-collection-search-input]') : null;
    const clearButton = searchContainer ? searchContainer.querySelector('[data-collection-search-clear]') : null;
    const statusEl = searchContainer ? searchContainer.querySelector('[data-collection-search-status]') : null;
    const statusText = statusEl ? statusEl.querySelector('[data-collection-search-status-text]') : null;
    const noResultsMessage = collectionRoot.querySelector('[data-collection-search-empty]');
    const gridContainer = collectionRoot.querySelector('[data-productgrid-items]');
    const flydown = searchContainer ? searchContainer.querySelector('[data-collection-search-flydown]') : null;
    const flydownResults = flydown ? flydown.querySelector('[data-live-search-results]') : null;
    const flydownPlaceholder = flydown ? flydown.querySelector('[data-live-search-placeholder]') : null;
    const searchButton = searchContainer ? searchContainer.querySelector('[data-live-search-submit]') : null;

    if (!searchInput) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    collectionSearchControllers.set(collectionRoot, controller);

    const sectionEl = collectionRoot.closest('[data-section-id]');
    const sectionId = sectionEl ? sectionEl.dataset.sectionId : null;
    const sectionDataEl = sectionEl ? sectionEl.querySelector('[data-section-data]') : null;
    let sectionData = {};

    if (sectionDataEl) {
      try {
        sectionData = JSON.parse(sectionDataEl.getAttribute('data-section-data') || sectionDataEl.innerHTML || '{}');
      } catch (error) {
        console.warn('Collection search: unable to parse section data', error);
      }
    }

    const collectionHandle = sectionData.context && sectionData.context.collectionHandle ? sectionData.context.collectionHandle : collectionRoot.dataset.collectionHandle || '';
    const collectionUrl = sectionData.context && sectionData.context.collectionUrl ? sectionData.context.collectionUrl : window.location.pathname;
    
    // For backward compatibility - keep grid filtering logic
    const initialItems = gridContainer ? Array.from(collectionRoot.querySelectorAll('[data-product-item]')) : [];
    const currentPage = gridContainer ? parseInt(gridContainer.dataset.currentPage || '1', 10) || 1 : 1;
    const totalPagesAttr = gridContainer ? parseInt(gridContainer.dataset.totalPages || '1', 10) || 1 : 1;
    const productsPerPageAttr = gridContainer ? parseInt(gridContainer.dataset.productsPerPage || '0', 10) : 0;
    const productsPerPage = productsPerPageAttr || initialItems.length || 1;
    const totalProducts = parseInt(sectionData.product_count || (productsPerPage * totalPagesAttr) || initialItems.length, 10);
    const totalPages = totalPagesAttr || Math.max(1, Math.ceil(totalProducts / Math.max(productsPerPage, 1)));

    const status = {
      update(message = '', { loading = false, error = false } = {}) {
        if (!statusEl || !statusText) return;
        if (!message) {
          statusEl.hidden = true;
          statusEl.dataset.loading = 'false';
          statusEl.classList.remove('collection-search__status--error');
          statusText.textContent = '';
          return;
        }

        statusText.textContent = message;
        statusEl.hidden = false;
        statusEl.dataset.loading = loading ? 'true' : 'false';
        statusEl.classList.toggle('collection-search__status--error', Boolean(error));
      }
    };

    const searchState = {
      sectionEl,
      sectionId,
      collectionUrl,
      collectionHandle,
      totalPages,
      loadedPages: new Set([currentPage]),
      loadingPromise: null,
      searchPromise: null,
      allPagesLoaded: totalPages <= 1 || collectionRoot.dataset.collectionSearchComplete === 'true',
      productHandleSet: new Set(initialItems.map(item => item.dataset.productHandle).filter(Boolean)),
      observer: null,
      lastTerm: '',
      reinitialized: false,
      status,
      flydownOpen: false
    };

    collectionSearchState.set(collectionRoot, searchState);

    const getProductItems = () => Array.from(collectionRoot.querySelectorAll('[data-product-item]'));
    let productItems = [];

    const decorateProductItems = () => {
      productItems = getProductItems();
      productItems.forEach(item => {
        const title = item.querySelector('[data-product-item-content]');
        const vendor = item.querySelector('.productitem--vendor');
        const tags = Array.from(item.querySelectorAll('[data-product-tag]'));

        item.dataset.collectionSearchTitle = (title ? title.textContent : '').trim().toLowerCase();
        item.dataset.collectionSearchVendor = (vendor ? vendor.textContent : '').trim().toLowerCase();
        item.dataset.collectionSearchTags = tags.map(tag => tag.textContent.trim().toLowerCase()).join(' ');

        if (item.dataset.productHandle) {
          searchState.productHandleSet.add(item.dataset.productHandle);
        }
      });
    };

    // Helper function to extract numeric part from search term (strips prefixes like "SY" from "SY123456")
    const extractNumericPart = (searchTerm) => {
      if (!searchTerm) return null;
      // Match pattern: letters followed by numbers (e.g., "SY123456" -> "123456")
      const match = searchTerm.match(/^[a-z]+(\d+)$/i);
      if (match && match[1]) {
        return match[1];
      }
      return null;
    };

    const applySearch = value => {
      const term = value.trim().toLowerCase();
      searchState.lastTerm = term;
      let visibleCount = 0;

      // Get numeric part if search term has a prefix pattern (e.g., "sy123456" -> "123456")
      const numericPart = extractNumericPart(term);

      productItems.forEach(item => {
        const title = item.dataset.collectionSearchTitle || '';
        const vendor = item.dataset.collectionSearchVendor || '';
        const tags = item.dataset.collectionSearchTags || '';
        
        // Check if original term matches
        let matches = term === '' || title.includes(term) || vendor.includes(term) || tags.includes(term);
        
        // If no match and we have a numeric part, also try searching with just the numbers
        if (!matches && numericPart) {
          matches = title.includes(numericPart) || vendor.includes(numericPart) || tags.includes(numericPart);
        }

        item.classList.toggle('collection-search__item--hidden', !matches);

        if (matches) {
          item.removeAttribute('hidden');
          item.removeAttribute('aria-hidden');
          visibleCount += 1;
        } else {
          item.setAttribute('hidden', '');
          item.setAttribute('aria-hidden', 'true');
        }
      });

      if (noResultsMessage) {
        const showNoResults = term !== '' && visibleCount === 0 && searchState.allPagesLoaded && !searchState.loadingPromise;
        noResultsMessage.hidden = !showNoResults;
      }

      if (clearButton) {
        clearButton.hidden = term === '';
      }
    };

    const reinitializeSection = () => {
      if (!searchState.sectionEl || !searchState.sectionId || searchState.reinitialized) {
        return;
      }

      searchState.reinitialized = true;
      const unloadEvent = new CustomEvent('shopify:section:unload', {
        detail: { sectionId: searchState.sectionId },
        bubbles: true
      });
      const loadEvent = new CustomEvent('shopify:section:load', {
        detail: { sectionId: searchState.sectionId },
        bubbles: true
      });

      searchState.sectionEl.dispatchEvent(unloadEvent);
      searchState.sectionEl.dispatchEvent(loadEvent);
    };

    const ensureAllPagesLoaded = () => {
      if (searchState.allPagesLoaded || searchState.totalPages <= searchState.loadedPages.size) {
        searchState.allPagesLoaded = true;
        collectionRoot.dataset.collectionSearchComplete = 'true';
        return Promise.resolve();
      }

      if (searchState.loadingPromise) {
        return searchState.loadingPromise;
      }

      if (!searchState.sectionId) {
        searchState.allPagesLoaded = true;
        collectionRoot.dataset.collectionSearchComplete = 'true';
        searchState.status.update('');
        return Promise.resolve();
      }

      const parser = new DOMParser();
      const baseUrl = new URL(searchState.collectionUrl, window.location.origin);
      const baseParams = new URLSearchParams(window.location.search);
      baseParams.delete('page');
      baseParams.delete('section_id');

      searchState.status.update('Loading more products…', { loading: true });

      const fetchPage = async page => {
        if (signal.aborted) return;

        const url = new URL(baseUrl);
        baseParams.forEach((value, key) => url.searchParams.append(key, value));
        url.searchParams.set('page', page);
        url.searchParams.set('section_id', searchState.sectionId);

        const response = await fetch(url.toString(), { signal });
        if (!response.ok) {
          throw new Error(`Failed to load collection page ${page}`);
        }

        const html = await response.text();
        const doc = parser.parseFromString(html, 'text/html');
        const itemsContainer = doc.querySelector('[data-productgrid-items]');

        if (!itemsContainer) return;

        const newItems = Array.from(itemsContainer.querySelectorAll('[data-product-item]'));
        if (!newItems.length) return;

        const fragment = document.createDocumentFragment();

        newItems.forEach(item => {
          const handle = item.dataset.productHandle;
          if (handle && searchState.productHandleSet.has(handle)) return;
          if (handle) searchState.productHandleSet.add(handle);
          fragment.appendChild(item);
        });

        if (fragment.childNodes.length) {
          gridContainer.appendChild(fragment);
        }
      };

      searchState.loadingPromise = (async () => {
        for (let page = 1; page <= searchState.totalPages; page += 1) {
          if (signal.aborted) return;
          if (searchState.loadedPages.has(page)) {
            continue;
          }

          await fetchPage(page);
          searchState.loadedPages.add(page);
        }

        if (signal.aborted) return;

        searchState.allPagesLoaded = true;
        collectionRoot.dataset.collectionSearchComplete = 'true';
        searchState.status.update('');
        decorateProductItems();
        applySearch(searchInput.value || '');
        reinitializeSection();
      })().catch(error => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Collection search pagination error', error);
        searchState.status.update('Some products could not be loaded. Showing partial results.', { error: true });
      }).finally(() => {
        searchState.loadingPromise = null;
        if (!searchState.allPagesLoaded && !signal.aborted) {
          searchState.status.update('');
        }
      });

      return searchState.loadingPromise;
    };

    decorateProductItems();
    applySearch(searchInput.value || '');

    if (gridContainer) {
      const observer = new MutationObserver(() => {
        if (signal.aborted) return;
        decorateProductItems();
        applySearch(searchInput.value || '');
      });

      observer.observe(gridContainer, { childList: true });
      searchState.observer = observer;

      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }

    // Live search functionality using predictive search API
    const performLiveSearch = async (term) => {
      if (!term || term.trim().length < 2) {
        closeFlydown();
        if (gridContainer) {
          applySearch(term);
        }
        return;
      }

      // Cancel previous search
      if (searchState.searchPromise) {
        const prevController = new AbortController();
        prevController.abort();
      }

      const currentController = new AbortController();
      searchState.searchPromise = { controller: currentController };

      try {
        if (flydownPlaceholder) {
          flydownPlaceholder.style.display = 'block';
        }
        if (flydownResults) {
          flydownResults.style.display = 'none';
        }
        openFlydown();

        const trimmedTerm = term.trim();
        const numericPart = extractNumericPart(trimmedTerm.toLowerCase());
        
        // Build predictive search URL with collection scope
        // Try original term first, but if it matches prefix pattern, also try numeric part
        let searchTerm = trimmedTerm;
        // If search term matches pattern like "SY123456", also try searching with just "123456"
        // We'll use the numeric part as the search term since Shopify search will find products containing those numbers
        if (numericPart && numericPart.length >= 2) {
          searchTerm = numericPart;
        }

        const params = new URLSearchParams({
          q: searchTerm,
          section_id: 'predictive-search',
          resources: 'product',
          'resources[limit]': '8',
          'resources[type]': 'product',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,product_id,handle,image,price',
          'resources[options][unavailable_products]': 'last'
        });

        // Add collection scope if available
        if (collectionHandle && collectionHandle !== 'all') {
          params.append('resources[options][scope]', `collection:${collectionHandle}`);
        }

        const url = `${window.Theme?.routes?.predictive_search_url || '/search/suggestions'}?${params.toString()}`;
        
        const response = await fetch(url, { 
          signal: currentController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const html = await response.text();

        if (currentController.signal.aborted) return;

          if (flydownResults) {
            flydownResults.innerHTML = html;
            
            // Initialize rimg for lazy loading if available
            if (window.rimg && typeof window.rimg.watch === 'function') {
              window.rimg.watch(flydownResults);
            }

            // Check if there are actual results
            const hasResults = flydownResults.querySelector('.search-flydown--product') || 
                            flydownResults.querySelector('.search-flydown--no-results') ||
                            flydownResults.querySelector('[data-live-search-no-products]');

            if (hasResults) {
              if (flydownPlaceholder) {
                flydownPlaceholder.style.display = 'none';
              }
              flydownResults.style.display = 'block';
              flydownResults.setAttribute('data-animation-state', 'visible');
              if (flydownPlaceholder) {
                flydownPlaceholder.setAttribute('data-animation-state', 'hidden');
              }
            } else {
              if (flydownPlaceholder) {
                flydownPlaceholder.style.display = 'block';
              }
              flydownResults.style.display = 'none';
            }

            updateFlydownHeight();
          }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Collection live search error:', error);
        if (flydownResults) {
          flydownResults.innerHTML = '<p class="search-flydown--no-results">Search temporarily unavailable</p>';
          flydownResults.style.display = 'block';
        }
        if (flydownPlaceholder) {
          flydownPlaceholder.style.display = 'none';
        }
      } finally {
        searchState.searchPromise = null;
      }
    };

    const debounceLiveSearch = (() => {
      let timeout;
      return (term) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          performLiveSearch(term);
        }, 250);
      };
    })();

    const openFlydown = () => {
      if (!flydown) return;
      searchState.flydownOpen = true;
      flydown.setAttribute('data-animation', 'closed=>open');
      flydown.setAttribute('data-animation-state', 'open');
      if (searchContainer) {
        searchContainer.classList.add('live-search--active');
      }
      updateFlydownHeight();
    };

    const closeFlydown = () => {
      if (!flydown) return;
      searchState.flydownOpen = false;
      flydown.setAttribute('data-animation', 'open=>closed');
      flydown.setAttribute('data-animation-state', 'closed');
      if (searchContainer) {
        searchContainer.classList.remove('live-search--active');
      }
      setTimeout(() => {
        if (flydownPlaceholder) {
          flydownPlaceholder.style.display = 'block';
        }
        if (flydownResults) {
          flydownResults.innerHTML = '';
          flydownResults.style.display = 'none';
        }
      }, 200);
    };

    const updateFlydownHeight = () => {
      if (!flydown) return;
      requestAnimationFrame(() => {
        const visibleContainer = flydown.querySelector('[data-animation-state="visible"]') ||
                                (flydownResults && flydownResults.style.display !== 'none' && flydownResults.innerHTML.trim() ? flydownResults : null) ||
                                (flydownPlaceholder && flydownPlaceholder.style.display !== 'none' ? flydownPlaceholder : null);
        
        if (visibleContainer) {
          const scrollHeight = visibleContainer.scrollHeight || visibleContainer.offsetHeight;
          flydown.style.setProperty('--open-height', `${scrollHeight}px`);
          
          const viewportHeight = document.documentElement.clientHeight;
          const flydownRect = flydown.getBoundingClientRect();
          const topPos = flydownRect.top + window.scrollY;
          const offset = 30;
          const maxHeight = Math.max(200, Math.min(600, viewportHeight - flydownRect.top - offset));
          flydown.style.setProperty('--flydown-max-height', `${maxHeight}px`);
        }
      });
    };

    // Event listeners
    searchInput.addEventListener('input', event => {
      const term = event.currentTarget.value || '';
      
      // Use live search if flydown is available, otherwise fall back to grid filtering
      if (flydown) {
        debounceLiveSearch(term);
      } else if (gridContainer) {
        applySearch(term);
        if (!searchState.allPagesLoaded && term.trim() !== '') {
          ensureAllPagesLoaded().then(() => {
            if (!signal.aborted) {
              applySearch(searchInput.value || '');
            }
          });
        }
      }
    }, { signal });

    searchInput.addEventListener('focus', () => {
      if (flydown && searchInput.value.trim().length >= 2) {
        openFlydown();
      }
      if (!searchState.allPagesLoaded && gridContainer) {
        ensureAllPagesLoaded();
      }
    }, { signal });

    // Close flydown when clicking outside
    document.addEventListener('click', (event) => {
      if (searchState.flydownOpen && searchContainer && !searchContainer.contains(event.target)) {
        closeFlydown();
      }
    }, { signal });

    // Handle escape key
    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchState.flydownOpen) {
        closeFlydown();
        searchInput.blur();
      }
    }, { signal });

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        searchInput.value = '';
        closeFlydown();
        if (gridContainer) {
          applySearch('');
        }
        searchInput.focus();
        searchState.status.update('');
      }, { signal });
    }

    if (searchButton) {
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        const term = searchInput.value.trim();
        if (term) {
          // Navigate to search results page scoped to collection
          const searchUrl = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin);
          searchUrl.searchParams.set('q', term);
          if (collectionHandle && collectionHandle !== 'all') {
            // Add collection filter to search
            searchUrl.searchParams.set('options[unavailable_products]', 'last');
          }
          window.location.href = searchUrl.toString();
        }
      }, { signal });
    }

    signal.addEventListener('abort', () => {
      searchState.status.update('');
      if (searchState.observer) {
        searchState.observer.disconnect();
      }
      collectionSearchState.delete(collectionRoot);
    }, { once: true });
  });
}

function initFilterGroups(root = document) {
  const filterGroups = root.querySelectorAll('[data-filter-group]');

  filterGroups.forEach(group => {
    const previousController = filterGroupControllers.get(group);
    if (previousController) {
      previousController.abort();
    }

    const filterList = group.querySelector('[data-filter-group-list]');
    if (!filterList) {
      return;
    }

    const filterItems = Array.from(filterList.querySelectorAll('[data-filter-item]'));
    if (!filterItems.length) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    filterGroupControllers.set(group, controller);

    const searchInput = group.querySelector('[data-filter-search-input]');
    const clearButton = group.querySelector('[data-filter-search-clear]');
    const emptyState = group.querySelector('[data-filter-search-empty]');
    const toggleButton = group.querySelector('[data-filter-toggle]');
    const initialLimit = parseInt(filterList.getAttribute('data-filter-initial-limit') || '8', 10);
    const accordionContent = group.querySelector('[data-accordion-content]');
    const accordionTrigger = group.querySelector('[data-filter-group-trigger]');

    const state = {
      searchTerm: '',
      expanded: false
    };

    const expandAccordionIfNeeded = () => {
      if (!accordionContent || !accordionTrigger) return;
      
      const isExpanded = accordionContent.classList.contains('filter-group-active') ||
                        accordionTrigger.classList.contains('productgrid--sidebar-button-active') ||
                        accordionTrigger.getAttribute('aria-expanded') === 'true';
      
      if (!isExpanded) {
        accordionTrigger.click();
      }
    };

    filterItems.forEach((item, index) => {
      item.dataset.filterIndex = String(index);
      // Get the label from data-filter-label attribute (use getAttribute for reliability)
      let searchText = item.getAttribute('data-filter-label') || item.dataset.filterLabel || '';
      if (!searchText) {
        // Fallback: extract text from the label span, excluding the count
        const labelSpan = item.querySelector('.collection-filters__filter-list-item-text');
        if (labelSpan) {
          searchText = labelSpan.textContent || '';
          // Remove the count in parentheses if present
          searchText = searchText.replace(/\s*\(\d+\)\s*$/, '').trim();
        } else {
          // Last resort: use all text content
          searchText = item.textContent || '';
        }
      }
      // Store the searchable text in lowercase for case-insensitive matching
      item.dataset.filterSearchText = searchText.trim().toLowerCase();
    });

    const updateToggle = matchesCount => {
      if (!toggleButton) return;

      const seeMoreText = toggleButton.dataset.seeMore || 'See more';
      const seeLessText = toggleButton.dataset.seeLess || 'See less';
      const shouldShowToggle = state.searchTerm === '' && filterItems.length > initialLimit;

      toggleButton.hidden = !shouldShowToggle;
      toggleButton.textContent = state.expanded ? seeLessText : seeMoreText;
      toggleButton.setAttribute('aria-expanded', state.expanded ? 'true' : 'false');
      toggleButton.disabled = !shouldShowToggle || matchesCount === 0;
    };

    const applyFilters = () => {
      const term = state.searchTerm.toLowerCase().trim();
      const hasSearch = term !== '';
      let visibleMatches = 0;

      filterItems.forEach(item => {
        const itemText = item.dataset.filterSearchText || '';
        const matchesSearch = !hasSearch || itemText.includes(term);
        const index = parseInt(item.dataset.filterIndex || '0', 10);
        const isActive = item.classList.contains('collection-filters__filter-list-item--active');

        let hiddenBySearch = false;
        let hiddenByCollapse = false;

        // If searching, only show items that match
        if (hasSearch && !matchesSearch) {
          hiddenBySearch = true;
        } 
        // If not searching, apply the collapse logic (show first N items or expanded items)
        else if (!hasSearch && !state.expanded && index >= initialLimit && !isActive) {
          hiddenByCollapse = true;
        }

        if (hiddenBySearch) {
          item.classList.add('collection-filters__filter-list-item--search-hidden');
          item.classList.remove('collection-filters__filter-list-item--collapsed');
          item.setAttribute('hidden', '');
          item.setAttribute('aria-hidden', 'true');
        } else if (hiddenByCollapse) {
          item.classList.add('collection-filters__filter-list-item--collapsed');
          item.classList.remove('collection-filters__filter-list-item--search-hidden');
          item.setAttribute('hidden', '');
          item.setAttribute('aria-hidden', 'true');
        } else {
          item.classList.remove('collection-filters__filter-list-item--collapsed', 'collection-filters__filter-list-item--search-hidden');
          item.removeAttribute('hidden');
          item.removeAttribute('aria-hidden');
          visibleMatches += 1;
        }
      });

      if (emptyState) {
        emptyState.hidden = state.searchTerm === '' || visibleMatches > 0;
      }

      if (clearButton) {
        clearButton.hidden = state.searchTerm === '';
      }

      updateToggle(visibleMatches);
    };

    applyFilters();

    if (searchInput) {
      searchInput.addEventListener('focus', () => {
        expandAccordionIfNeeded();
      }, { signal });

      const handleSearch = (event) => {
        expandAccordionIfNeeded();
        const value = (event.currentTarget?.value || event.target?.value || '').trim();
        state.searchTerm = value;
        applyFilters();
      };

      // Use both input and keyup events for better compatibility
      searchInput.addEventListener('input', handleSearch, { signal });
      searchInput.addEventListener('keyup', handleSearch, { signal });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        state.searchTerm = '';
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
        }
        state.expanded = false;
        applyFilters();
      }, { signal });
    }

    if (toggleButton) {
      toggleButton.addEventListener('click', () => {
        state.expanded = !state.expanded;
        applyFilters();
      }, { signal });
    }

    signal.addEventListener('abort', () => {
      filterGroupControllers.delete(group);
    }, { once: true });
  });
}

function initCollectionEnhancements(root = document) {
  initCollectionSearch(root);
  initFilterGroups(root);
}

document.addEventListener('DOMContentLoaded', function() {
  const desktopSearch = document.querySelector('.desktop-live-search');
  const mobileSearchContainer = document.querySelector('.mobile-live-search');

  function moveLiveSearch() {
    if (!desktopSearch || !mobileSearchContainer) return;

    if (window.innerWidth < 1300) {
      if (!mobileSearchContainer.contains(desktopSearch)) {
        mobileSearchContainer.appendChild(desktopSearch);
      }
    } else {
      const headerRight = document.querySelector('.site-header-right');
      if (headerRight && !headerRight.contains(desktopSearch)) {
        headerRight.insertBefore(desktopSearch, headerRight.firstChild);
      }
    }
  }

  moveLiveSearch();

  window.addEventListener('resize', moveLiveSearch);

  document.querySelectorAll('.pdp_specification-tab').forEach(tab => {
    tab.addEventListener('click', function() {
      const tabId = this.getAttribute('tab_id');
      const content = document.querySelector(`.pdp_specification-tab-content[data_tab="${tabId}"]`);

      // Remove active class from all tabs
      document.querySelectorAll('.pdp_specification-tab').forEach(t => t.classList.remove('active'));
      // Hide all tab content
      document.querySelectorAll('.pdp_specification-tab-content').forEach(c => c.style.display = 'none');
      
      // Add active class to clicked tab
      this.classList.add('active');
      
      // Show the corresponding content
      if (content) {
        content.style.display = 'block';
      }
    });
  });

  // Initialize: Show first tab and mark it as active
  const firstTab = document.querySelector('.pdp_specification-tab');
  const firstTabContent = firstTab ? document.querySelector(`.pdp_specification-tab-content[data_tab="${firstTab.getAttribute('tab_id')}"]`) : null;
  if (firstTab && firstTabContent) {
    firstTab.classList.add('active');
    firstTabContent.style.display = 'block';
  }

  initCollectionEnhancements(document);

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;
        initCollectionEnhancements(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', event => {
    initCollectionEnhancements(event.target || document);
  });

  document.addEventListener('shopify:section:select', event => {
    initCollectionEnhancements(event.target || document);
  });

  // Show overlay when live search is active (same as mega menu)
  function handleSearchOverlay() {
    const liveSearchElements = document.querySelectorAll('[data-live-search]');
    const dimmer = document.querySelector('[data-site-main-dimmer]');
    
    if (!dimmer) return;

    function updateDimmerState() {
      // Check if any live search is active
      const anySearchActive = Array.from(liveSearchElements).some(el => 
        el.classList.contains('live-search--active')
      );
      
      // Check if mega menu is already using the dimmer
      const meganavOpen = document.querySelector('.navmenu-submenu.navmenu-meganav[data-animation-state="open"]');
      
      if (anySearchActive && !meganavOpen) {
        // Show the dimmer with the same animation as mega menu
        dimmer.setAttribute('data-animation', 'closed=>open');
        dimmer.setAttribute('data-animation-state', 'open');
      } else if (!anySearchActive && !meganavOpen) {
        // Hide the dimmer only if mega menu is not using it
        dimmer.setAttribute('data-animation', 'open=>closed');
        // Remove the open state after animation completes
        setTimeout(() => {
          const stillActive = Array.from(liveSearchElements).some(el => 
            el.classList.contains('live-search--active')
          );
          const meganavStillOpen = document.querySelector('.navmenu-submenu.navmenu-meganav[data-animation-state="open"]');
          
          if (!stillActive && !meganavStillOpen) {
            dimmer.removeAttribute('data-animation');
            dimmer.removeAttribute('data-animation-state');
          }
        }, 200);
      }
    }

    liveSearchElements.forEach(liveSearch => {
      const observer = new MutationObserver(() => {
        updateDimmerState();
      });

      observer.observe(liveSearch, {
        attributes: true,
        attributeFilter: ['class']
      });
    });

    // Initial check
    updateDimmerState();
  }

  handleSearchOverlay();

  // Intercept predictive search API calls to handle prefix-stripped item numbers
  // This modifies search queries like "SY123456" to also try "123456"
  (function() {
    // Helper function to extract numeric part from search term
    const extractNumericPart = (searchTerm) => {
      if (!searchTerm) return null;
      // Match pattern: letters followed by numbers (e.g., "SY123456" -> "123456")
      const match = searchTerm.match(/^[a-z]+(\d+)$/i);
      if (match && match[1]) {
        return match[1];
      }
      return null;
    };

    // Store original fetch function
    const originalFetch = window.fetch;

    // Override fetch to intercept predictive search requests
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a predictive search request
      if (typeof url === 'string' && (
          url.includes('section_id=predictive-search') || 
          url.includes('/search/suggestions') ||
          (url.includes('predictive') && url.includes('search'))
        )) {
        
        try {
          // Handle both absolute and relative URLs
          const urlObj = url.startsWith('http') 
            ? new URL(url) 
            : new URL(url, window.location.origin);
          
          const searchParams = urlObj.searchParams;
          const originalQuery = searchParams.get('q');
          
          if (originalQuery) {
            // Decode the query first (it might be encoded)
            const decodedQuery = decodeURIComponent(originalQuery);
            
            // Handle queries with filters (e.g., "tag:something AND SY123456")
            // Extract the user's search term (part after " AND ")
            let userSearchTerm = decodedQuery;
            let filterPrefix = '';
            
            if (decodedQuery.includes(' AND ')) {
              const parts = decodedQuery.split(' AND ');
              if (parts.length >= 2) {
                filterPrefix = parts[0] + ' AND ';
                userSearchTerm = parts.slice(1).join(' AND '); // In case there are multiple ANDs
              }
            }
            
            const trimmedTerm = userSearchTerm.trim().toLowerCase();
            const numericPart = extractNumericPart(trimmedTerm);
            
            // If search term matches prefix pattern (e.g., "SY123456"), try numeric part instead
            if (numericPart && numericPart.length >= 2) {
              // Replace the user's search term with just the numeric part, keeping any filter prefix
              const newQuery = filterPrefix ? filterPrefix + numericPart : numericPart;
              searchParams.set('q', newQuery);
              args[0] = urlObj.toString();
            }
          }
        } catch (e) {
          // If URL parsing fails, continue with original request
          console.warn('Search query modification failed:', e);
        }
      }
      
      // Call original fetch
      return originalFetch.apply(this, args);
    };
  })();
});